import { useCallback, useEffect, useState } from 'react';
import type { AdminWhatsappTemplateRow } from '../../../domain/superadmin/types';
import { useAppContext } from '../../../contexts/AppContext';
import { fetchAdminWhatsappTemplates, updateAdminWhatsappTemplateDelivery } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

export function useAdminWhatsappTemplates() {
  const [items, setItems] = useState<AdminWhatsappTemplateRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const handleForbidden = useCallback(() => {
    setCurrentPage('dashboard');
    notify.warning('Acesso não autorizado à área de superadmin.');
  }, [setCurrentPage]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminWhatsappTemplates();
      setItems(result.items);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        handleForbidden();
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar templates de WhatsApp');
    } finally {
      setIsLoading(false);
    }
  }, [handleForbidden]);

  useEffect(() => {
    void load();
  }, [load]);

  const setDeliveryActive = useCallback(
    async (key: string, deliveryActive: boolean) => {
      const prev = items;
      setItems((cur) =>
        cur.map((t) => (t.key === key ? { ...t, deliveryActive } : t)),
      );
      try {
        const updated = await updateAdminWhatsappTemplateDelivery(key, deliveryActive);
        setItems((cur) => cur.map((t) => (t.key === key ? updated : t)));
      } catch (e) {
        setItems(prev);
        if (e instanceof SuperadminApiError && e.isForbidden) {
          handleForbidden();
          return;
        }
        notify.error(e instanceof Error ? e.message : 'Falha ao atualizar template');
      }
    },
    [items, handleForbidden],
  );

  return { items, isLoading, error, refetch: load, setDeliveryActive };
}
